import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Breadcrumb from "../Breadcrumb";
import Modal from "../Modal/Modal";
import { logoutUser } from "../../actions/user";
import { validateName, validateEmail, validateLoginId, validatePassword } from "../../helpers/validation";
import "./AdminCreate.css";

const AdminCreate = React.memo(({ isFetching, user_info, createAdmin, fetchUserInfo }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    useEffect(() => {
        fetchUserInfo();
    }, [fetchUserInfo]);

    const [form, setForm] = useState({
        login_id: "",
        password: "",
        admin_name: "",
        otp_enable: false,
        mail_address: ""
    });

    const [errors, setErrors] = useState({
        login_id: "",
        password: "",
        admin_name: "",
        mail_address: ""
    });
    const [showPassword, setShowPassword] = useState(false);
    const [modalMessage, setModalMessage] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);

     // 入力変更処理
    const handleChange = (e) => {
        const { name, value } = e.target;

        setForm({ ...form, [name]: value });

        // Clear error of the field being edited
        if (errors[name]) {
            setErrors({ ...errors, [name]: "" });
        }
    };

    // OTP チェックボックス変更
    const handleCheckbox = () => {
        const newOtp = !form.otp_enable;

        setForm({ ...form, otp_enable: newOtp });

        // OTP OFF → clear email error
        if (!newOtp && errors.mail_address) {
            setErrors({ ...errors, mail_address: "" });
        }
    };

    // 登録ボタン押下時処理
    const handleSubmit = async () => {
        const newErrors = {};

        // --- Login ID ---
        const loginIdError = validateLoginId(form.login_id);
        if (loginIdError) newErrors.login_id = loginIdError;

        // --- Password ---
        const passwordError = validatePassword(form.password);
        if (passwordError) newErrors.password = passwordError;

        // --- User Name ---
        const adminNameError = validateName(form.admin_name);
        if (adminNameError) newErrors.admin_name = adminNameError;

        // --- Email (OTP ON の場合のみチェック) ---
        if (form.otp_enable) {
            const emailError = validateEmail(form.mail_address);
            if (emailError) newErrors.mail_address = emailError;
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Clear all errors
        setErrors({ login_id: "", password: "", admin_name: "", mail_address: "" });

        const result = await createAdmin(form);

        // 成功時 → 一覧画面へ遷移
        if (result?.payload) {
            navigate("/admin-list");
            return;
        }

        // 失敗時 → モーダル表示
        if (result?.error) {
            setModalMessage(result.error || "エラーが発生しました。");
            setIsModalOpen(true);
        }
    };

    return (
        <div id="Main">
            <div id="Contents">
                <Breadcrumb 
                    user={user_info?.admin_name} 
                    onLogout={() => dispatch(logoutUser())}
                />

                {isModalOpen && (
                    <Modal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        message={modalMessage}
                    />
                )}
                
                <div className="admin-layout">
                    <div className="admin-card">
                        <h2 className="admin-create-title">管理者新規作成</h2>

                        <div className="btn-row">
                            <button className="btn-submit" onClick={handleSubmit}>登録</button>
                            <button className="btn-back" onClick={() => navigate("/admin-list")}>戻る</button>
                        </div>
                        
                        <div className="admin-create-form">
                            {/* LOGIN ID */}
                            <div className="form-row">
                                <label>ログインID</label>
                                <input name="login_id" value={form.login_id} onChange={handleChange} />
                            </div>
                            {errors.login_id && <p className="error-text">{errors.login_id}</p>}

                            {/* PASSWORD */}
                            <div className="form-row">
                                <label>パスワード</label>
                                <div className="password-wrapper">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        value={form.password}
                                        onChange={handleChange}
                                    />
                                    <span className="toggle-password" onClick={() => setShowPassword(!showPassword)}>
                                        <img
                                            src={showPassword ? "/img/common/show-password.svg" : "/img/common/hidden-password.svg"}
                                            alt="toggle"
                                            className="toggle-password-icon"
                                        />
                                    </span>
                                </div>
                            </div>
                            {errors.password && <p className="error-text">{errors.password}</p>}

                            {/* ADMIN NAME */}
                            <div className="form-row">
                                <label>ユーザー名</label>
                                <input name="admin_name" value={form.admin_name} onChange={handleChange} />
                            </div>
                            {errors.admin_name && <p className="error-text">{errors.admin_name}</p>}

                            {/* OTP ENABLE */}
                            <div className="form-row">
                                <label>二段階認証</label>
                                <div className="checkbox-row">
                                    <input
                                        type="checkbox"
                                        checked={form.otp_enable}
                                        onChange={handleCheckbox}
                                    />
                                    <span>二段階認証を使用する</span>
                                </div>
                            </div>

                            {/* EMAIL ADDRESS */}
                            <div className={`mail-animate ${form.otp_enable ? "show" : ""}`}>
                                <div className="form-row">
                                    <label className="admin-edit-label">メールアドレス</label>
                                    <input
                                        name="mail_address"
                                        value={form.mail_address}
                                        onChange={handleChange}
                                    />
                                </div>
                                {errors.mail_address && (
                                    <p className="error-text">{errors.mail_address}</p>
                                )}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
            <div className={`overlay ${isFetching ? 'active' : ''}`}>
                <div className={`spinner ${isFetching ? 'active' : ''}`}></div>
            </div>
        </div>
    );
});

AdminCreate.displayName = "AdminCreate";    // fix eslint(react/display-name)

AdminCreate.propTypes = {
    isFetching: PropTypes.bool.isRequired,
    user_info: PropTypes.object,
    createAdmin: PropTypes.func.isRequired,
    fetchUserInfo: PropTypes.func.isRequired,
};

export default AdminCreate;
